<template>
  <div class="terminals-geo">
    <div class="terminals-geo-panel public-container public-container-mobile">
      <div class="terminals-geo-panel-wrapper flex">
        <city-autocomplete
          large
          v-model="locationGuid"
          only-rcl-search
          placeholder="Введите название города"
          @change="onChangeLocation"
        />
        <vz-button
          large
          outline
          class="ml-20"
          variant="primary"
          @click="openTerminalsLocationModal"
        >
          Список городов
        </vz-button>
      </div>
    </div>
    <div class="terminals-geo-map">
      <div class="public-container public-container-mobile">
        <contacts-terminals-card
          closable
          :terminal-guid="selectedTerminal?.guid"
          class="terminals-geo-card"
          @close="onClose"
        />
      </div>
      <client-only>
        <vz-yandex-map
          v-model="map"
          v-model:location="locationValue"
          :markers="markerList"
          :controls="['zoomControl']"
          has-tooltip
          @marker-click="onTerminalSelect"
          @address-select="onResultSelect"
        >
        <template #map-additional>
          <yandex-map-controls :settings="{ position: 'left' }">
            <yandex-map-zoom-control />
          </yandex-map-controls>
          <yandex-map-marker v-if="customAddress" :settings="{ coordinates: customAddress }">
            <div class="marker-icon">
              <vz-icon
                url="/svg/marker.svg"
                class="marker-icon-default svg-info"
                size="40"
              />
            </div>
          </yandex-map-marker>
        </template>
        </vz-yandex-map>
      </client-only>
    </div>
  </div>
  <vz-terminals-location-modal @change="onChange" />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { Terminal, Location } from '~~/types/types'
import { useTerminalStore } from '~/stores/terminal'
import VzYandexMap from '~~/components/ui-kit/map/vz-yandex-map.vue'
import CityAutocomplete from '~/components/autocomplete/city-autocomplete.vue'
import VzTerminalsLocationModal from '~/components/modals/vz-terminals-location-modal.vue'
import ContactsTerminalsCard from '~/components/public/contacts/contacts-terminals-card.vue'
import type {LngLat, YMap, YMapLocationRequest} from '@yandex/ymaps3-types';
import {YandexMapControls, YandexMapMarker, YandexMapZoomControl} from 'vue-yandex-maps';

const emit = defineEmits(['terminal-select'])

const { $showDialog } = useNuxtApp()
const { terminalList } = storeToRefs(useTerminalStore())
const route = useRoute()
const initCoords: LngLat = [60.15, 58.25]

const map = shallowRef<null | YMap>(null)
const locationValue = ref<YMapLocationRequest>({
  center: initCoords, // starting position [lng, lat]
  zoom: 4, // starting zoom
});

const locationGuid = ref('')
const selectedTerminal = ref<Terminal | null>()
const customAddress = ref<LngLat>()

onMounted(() => {
  if (route.query.terminalGuid) {
    nextTick(() => {
      const terminal = markerList.value.find(v => v.guid === route.query.terminalGuid)
      if (terminal) {
        locationGuid.value = terminal.location.guid
        onChangeLocation(terminal.location.guid, terminal.location)
        onTerminalSelect(terminal)
      }
    })
  }
})

const markerList = computed(() => {
  return terminalList.value.map(terminal => ({
    ...terminal,
    coordinates: [...terminal.coordinates].reverse()
  }))
})

function onChange(location: Location) {
  locationGuid.value = location.guid
  onChangeLocation(location.guid, location)
}

function onChangeLocation(locationGuid: String, location: Location) {
  const coordinates = (Array.isArray(location.coordinates) ? location.coordinates : initCoords) as LngLat

  locationValue.value = {
    center: coordinates,
    zoom: location?.zoom || 10,
    duration: 500
  }
  selectedTerminal.value = null
}

function onTerminalSelect(terminal: Terminal): void {
  selectedTerminal.value = terminal
  locationValue.value = {
    center: terminal.coordinates,
    zoom: map.value?.zoom || 10,
    duration: 500
  }

  emit('terminal-select', terminal.location)
}

function openTerminalsLocationModal(): void {
  $showDialog({ name: 'terminals-location' })
}

function onResultSelect(coords: LngLat) {
  selectedTerminal.value = null
  customAddress.value = coords
}

function onClose() {
  selectedTerminal.value = null
}
</script>
